import { useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { sdlc1, sdlc2, sdlc3, sdlc4, sdlc5, sdlc6 } from "@/assets/images";

const SLIDES = [
  { src: sdlc1, stage: "Plan",    title: "Requirement mapping",     subtitle: "Structured discovery, crisp specs, measurable outcomes." },
  { src: sdlc2, stage: "Design",  title: "Architecture & UX design", subtitle: "System diagrams, wireframes, and design tokens set early." },
  { src: sdlc3, stage: "Build",   title: "Agile development",         subtitle: "Sprint-based delivery with pair programming & code review." },
  { src: sdlc4, stage: "Test",    title: "Automated QA",             subtitle: "Unit, integration, E2E tests run in CI on every commit." },
  { src: sdlc5, stage: "Deploy",  title: "Secure CI/CD",             subtitle: "Push-to-deploy pipelines with signed artifacts & audit logs." },
  { src: sdlc6, stage: "Operate", title: "24x7 monitoring",          subtitle: "Observability, incident response, continuous improvement." },
];

export default function SDLCCarousel() {
  const autoplay = useRef(Autoplay({ delay: 3500, stopOnInteraction: false, stopOnMouseEnter: true }));
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" }, [autoplay.current]);
  const [selected, setSelected] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    setCount(emblaApi.scrollSnapList().length);
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  return (
    <section className="border-y border-slate-200 bg-white" data-testid="sdlc-carousel-section">
      <div className="container-section py-16 md:py-24">
        <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0046B5]">Our SDLC</span>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              A disciplined software development life cycle, end to end.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Every Ortelly product is built on a mature, secure, and fully traceable SDLC — from requirement to 24x7 operations.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => emblaApi && emblaApi.scrollPrev()} aria-label="Previous slide" data-testid="carousel-prev" className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-[#0046B5] hover:text-[#0046B5]">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button onClick={() => emblaApi && emblaApi.scrollNext()} aria-label="Next slide" data-testid="carousel-next" className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-[#0046B5] hover:text-[#0046B5]">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg" ref={emblaRef} data-testid="sdlc-carousel">
          <div className="flex">
            {SLIDES.map((s, i) => (
              <div key={i} className="relative mr-4 min-w-0 flex-[0_0_85%] sm:flex-[0_0_55%] lg:flex-[0_0_42%]" data-testid={`sdlc-slide-${i}`}>
                <div className="group relative aspect-[4/3] overflow-hidden rounded-lg border border-slate-200 bg-slate-100 shadow-sm">
                  <img src={s.src} alt={s.title} className="h-full w-full object-cover transition-transform duration-[2200ms] ease-out group-hover:scale-105" loading="lazy" />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/20 to-transparent" />
                  <div className="absolute left-4 top-4 md:left-5 md:top-5">
                    <span className="inline-flex rounded-md bg-white/90 px-2.5 py-1 font-[Outfit] text-[11px] font-bold uppercase tracking-[0.15em] text-[#0046B5] backdrop-blur">
                      {s.stage}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                    <h3 className="font-[Outfit] text-lg font-semibold leading-tight text-white md:text-xl">{s.title}</h3>
                    <p className="mt-1 text-xs text-slate-200 md:text-sm">{s.subtitle}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2" role="tablist">
          {Array.from({ length: count }).map((_, i) => (
            <button
              key={i}
              onClick={() => emblaApi && emblaApi.scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`sdlc-dot-${i}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === selected ? "w-8 bg-[#0046B5]" : "w-3 bg-slate-300 hover:bg-slate-400"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
